import { formatFallback } from '@/lib/crm/utils/labelFormatters';

/** Label displayed for null/missing date values in reports */
export const NO_DATE_LABEL = '(No date)';

/** Plain number formatter (not currency-dependent) */
export const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

/** Compact number formatter for axis labels (e.g., 1.5K, 2M) */
export const compactNumberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/**
 * Percentage formatter. CRM percentage properties store a fraction (0.35), so
 * `style: 'percent'` — which multiplies by 100 — is the correct reader: 0.35
 * renders as "35%".
 */
export const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});

// --- Currency-aware formatters (cached per currency code) ---

const currencyFmtCache = new Map<string, Intl.NumberFormat>();
const compactCurrencyFmtCache = new Map<string, Intl.NumberFormat>();

/**
 * Normalize currency code to prevent Intl.NumberFormat errors.
 * Empty strings and falsy values default to 'USD'.
 *
 * Note: 3-letter non-ISO codes (e.g., 'XYZ') are accepted by Intl.NumberFormat
 * and displayed as currency prefixes. Invalid formats (wrong length, special
 * characters, numeric) throw RangeError and are caught by the formatter factories.
 */
export function normalizeCurrency(currency: string | undefined): string {
  return currency && currency.trim() !== '' ? currency.trim() : 'USD';
}

/** Get a cached currency formatter for the given currency code. */
export function getCurrencyFormatter(currency = 'USD'): Intl.NumberFormat {
  const normalized = normalizeCurrency(currency);
  let fmt = currencyFmtCache.get(normalized);
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: normalized,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    } catch (error) {
      console.warn('[reports] Invalid currency code - falling back to USD', {
        component: 'reports',
        currency: normalized,
        error: error instanceof Error ? error.message : String(error),
      });
      // Invalid currency code throws RangeError - fall back to USD
      fmt = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      currencyFmtCache.set('USD', fmt);
    }
    if (!currencyFmtCache.has(normalized)) {
      currencyFmtCache.set(normalized, fmt);
    }
  }
  return fmt;
}

/** Get a cached compact currency formatter for axis labels (e.g., $50K, €1.2M). */
export function getCompactCurrencyFormatter(
  currency = 'USD'
): Intl.NumberFormat {
  const normalized = normalizeCurrency(currency);
  let fmt = compactCurrencyFmtCache.get(normalized);
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: normalized,
        notation: 'compact',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      });
    } catch (error) {
      console.warn('[reports] Invalid currency code - falling back to USD', {
        component: 'reports',
        currency: normalized,
        error: error instanceof Error ? error.message : String(error),
      });
      // Invalid currency code throws RangeError - fall back to USD
      fmt = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      });
      compactCurrencyFmtCache.set('USD', fmt);
    }
    if (!compactCurrencyFmtCache.has(normalized)) {
      compactCurrencyFmtCache.set(normalized, fmt);
    }
  }
  return fmt;
}

/** Check if a Cube.js measure key represents a currency value. */
export function isCurrencyMeasure(key: string): boolean {
  // Match amount/value/revenue as complete word segments (not substrings)
  // Treat dots, underscores, start, and end as word boundaries
  const hasKeyword = /(^|[._])(amount|value|revenue)($|[._])/i.test(key);
  if (!hasKeyword) {
    return false;
  }

  // Exclude non-currency measures: count/number indicators before keyword, or count/id/type after
  const isNonCurrency =
    /(number|count|num|parameter|config|setting)(_of)?[._](amount|value|revenue)($|[._])/i.test(
      key
    ) ||
    /(^|[._])(amount|value|revenue)[._](count|number|num|id|type|status)/i.test(
      key
    );
  return !isNonCurrency;
}

/**
 * Format a numeric measure value based on its key.
 * Currency measures (amount/value/revenue) → "$50,000" / "€50.000"
 * Other measures → "1,234.56"
 *
 * Pass `columnType === 'currency'` (from the backend column metadata) to force
 * currency formatting even when the key name doesn't match the heuristic.
 * `isCurrencyMeasure` remains the fallback when no explicit type is supplied.
 *
 * `columnType === 'percentage'` renders the stored fraction as a percentage
 * ("35%" for 0.35). The explicit column type is the only signal — there is no
 * key-name heuristic for percentages, and there should not be one.
 */
export function formatMeasureValue(
  value: number,
  key: string,
  currency = 'USD',
  columnType?: string
): string {
  if (columnType === 'percentage') {
    return percentFormatter.format(value);
  }
  const isCurrency = columnType === 'currency' || isCurrencyMeasure(key);
  return isCurrency
    ? getCurrencyFormatter(currency).format(value)
    : numberFormatter.format(value);
}

/** Valid granularity suffixes for date dimensions */
export const GRANULARITY_SUFFIXES = [
  'day',
  'week',
  'month',
  'quarter',
  'year',
] as const;
export type GranularitySuffix = (typeof GRANULARITY_SUFFIXES)[number];

/** Extract granularity suffix from dimension string if present */
export function extractGranularityFromDimension(dimension: string): {
  baseDimension: string;
  granularity: GranularitySuffix | null;
} {
  for (const suffix of GRANULARITY_SUFFIXES) {
    if (dimension.endsWith(`.${suffix}`)) {
      return {
        baseDimension: dimension.slice(0, -(suffix.length + 1)),
        granularity: suffix,
      };
    }
  }
  return { baseDimension: dimension, granularity: null };
}

/** Check if a dimension name has a granularity suffix (is a date dimension) */
export function isDateDimensionWithGranularity(dimensionName: string): boolean {
  return GRANULARITY_SUFFIXES.some(suffix =>
    dimensionName.endsWith(`.${suffix}`)
  );
}

/**
 * Format a date string using granularity-appropriate formatting.
 *
 * - day     → "Mar 15, 2024"
 * - week    → "Mar 15, 2024" (week starting date)
 * - month   → "Mar 2024"
 * - quarter → "Q1 2024"
 * - year    → "2024"
 * - null granularity → "Mar 15, 2024" (default)
 */
export function formatDateByGranularity(
  isoString: string,
  granularity: GranularitySuffix | null
): string {
  try {
    // Ensure UTC parsing: append 'Z' if the string lacks a timezone indicator.
    // Without this, strings like '2024-04-01T00:00:00.000' are parsed as local
    // time, which can shift the date backward and produce wrong quarters/months.
    const normalized = /(Z|[+-]\d{2}:\d{2}|[+-]\d{4})$/i.test(isoString)
      ? isoString
      : isoString + 'Z';
    const d = new Date(normalized);
    if (isNaN(d.getTime())) {
      return isoString;
    }

    switch (granularity) {
      case 'year':
        return d.toLocaleDateString('en-US', {
          year: 'numeric',
          timeZone: 'UTC',
        });
      case 'quarter': {
        const month = d.getUTCMonth(); // 0-11
        const q = Math.floor(month / 3) + 1;
        return `Q${q} ${d.getUTCFullYear()}`;
      }
      case 'month':
        return d.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          timeZone: 'UTC',
        });
      case 'week':
      case 'day':
      default:
        return d.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        });
    }
  } catch {
    return isoString;
  }
}

/**
 * Format an x-axis label value for chart display.
 *
 * Uses the same logic as formatCellValue (used for table cells) to ensure
 * consistent formatting across chart and table views. The column type from
 * the Cube.js schema determines how values are formatted:
 *
 * - time columns → granularity-aware date formatting
 * - Machine identifiers (ahoy_*, snake_case) → Title Case via resolveLabel/formatFallback
 * - Null/missing dates → "(No date)"
 * - Everything else → pass through as-is (including resolveLabel output, verbatim)
 */
export function formatXLabel(
  value: unknown,
  dimensionName?: string,
  resolveLabel?: (value: string, dimensionName?: string) => string,
  columnType?: string
): string {
  // Null handling. An empty string is a missing group value too — passing it
  // through produced blank axis ticks, legend entries, and tooltip names.
  // As a GROUP label the missing bucket is a real category, so it reads
  // "None" — matching empty association groups — rather than the em-dash
  // tables use for missing cell values.
  if (value == null || value === '' || value === 'null' || value === '∅') {
    if (dimensionName && isDateDimensionWithGranularity(dimensionName)) {
      return NO_DATE_LABEL;
    }
    return 'None';
  }

  // Defensive check: prevent objects from being coerced to "[object Object]"
  if (typeof value === 'object' && value !== null) {
    console.warn(
      '[reports] Object value in chart label - replacing with em-dash',
      {
        component: 'reports',
        dimensionName,
        valueType: Object.prototype.toString.call(value),
      }
    );
    return '\u2014';
  }

  const strValue = String(value);

  // The native query engine labels an empty association group with the
  // literal "(none)" — surface it as plain "None" instead of the
  // parenthesized machine label.
  if (strValue === '(none)') {
    return 'None';
  }

  // Time columns: format using granularity from dimension name.
  // Detect via explicit columnType OR ISO date pattern (chartPivot x values).
  if (columnType === 'time' || /^\d{4}-\d{2}-\d{2}T/.test(strValue)) {
    const { granularity } = dimensionName
      ? extractGranularityFromDimension(dimensionName)
      : { granularity: null };
    return formatDateByGranularity(strValue, granularity);
  }

  // Try resolveLabel if provided
  if (resolveLabel) {
    const resolved = resolveLabel(strValue, dimensionName);
    if (resolved !== strValue) {
      return resolved;
    }
  }

  // Machine identifiers: ahoy_ prefix or snake_case without spaces
  if (
    strValue.startsWith('ahoy_') ||
    (strValue.includes('_') && !strValue.includes(' '))
  ) {
    return formatFallback(strValue);
  }

  return strValue;
}

/**
 * Format a table cell value based on column type and key.
 *
 * - time columns  → locale date string
 * - number columns with amount/value key → currency
 * - other numbers → locale number
 * - strings       → resolveLabel (pipeline ↦ "Sales Pipeline", etc.)
 * - null / "null" → em-dash
 */
export function formatCellValue(
  value: unknown,
  key: string,
  columnType: string | undefined,
  resolveLabel: (value: string, dimensionName?: string) => string,
  currency = 'USD'
): string {
  // Null / undefined / literal "null" string from Cube.js
  if (value == null || value === 'null') {
    return isDateDimensionWithGranularity(key) ? NO_DATE_LABEL : '\u2014';
  }

  const strValue = String(value);

  // Empty association groups arrive as the literal "(none)" \u2014 see formatXLabel.
  if (strValue === '(none)') {
    return 'None';
  }

  // Time columns: ISO dates like "2024-03-15T00:00:00.000"
  if (columnType === 'time' || /^\d{4}-\d{2}-\d{2}T/.test(strValue)) {
    try {
      // Normalize ISO string to ensure UTC interpretation
      const normalized = /(Z|[+-]\d{2}:\d{2}|[+-]\d{4})$/i.test(strValue)
        ? strValue
        : strValue + 'Z';
      const d = new Date(normalized);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        });
      }
    } catch {
      // fall through
    }
  }

  // Currency columns: the backend tags the column type "currency". Honor it even
  // when the pivoted cell value arrives as a string (nativeToTablePivot stringifies
  // values), which is why the numeric branch below never fires for these columns.
  if (columnType === 'currency') {
    const num = typeof value === 'number' ? value : parseFloat(strValue);
    if (!isNaN(num)) {
      return getCurrencyFormatter(currency).format(num);
    }
  }

  // Percentage columns: same stringified-cell problem as currency above, so this
  // has to parse the string rather than lean on the numeric branch below.
  if (columnType === 'percentage') {
    const num = typeof value === 'number' ? value : parseFloat(strValue);
    if (!isNaN(num)) {
      return percentFormatter.format(num);
    }
  }

  // Numeric columns: format as currency for amount/value fields, plain number otherwise
  if (columnType === 'number' || typeof value === 'number') {
    const num = typeof value === 'number' ? value : parseFloat(strValue);
    if (!isNaN(num)) {
      if (isCurrencyMeasure(key)) {
        return getCurrencyFormatter(currency).format(num);
      }
      return numberFormatter.format(num);
    }
  }

  // Machine identifiers: apply full label resolution + formatFallback
  // (values prefixed with "ahoy_" or containing underscores without spaces).
  const isMachineId =
    strValue.startsWith('ahoy_') ||
    (strValue.includes('_') && !strValue.includes(' '));
  if (isMachineId) {
    const resolved = resolveLabel(strValue, key);
    if (resolved !== strValue) {
      return resolved;
    }
    return formatFallback(strValue);
  }

  return strValue;
}
